import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Player = { id: string; name: string };

const MatchmakingPage: React.FC = () => {
  const { lobbyId } = useParams();
  const navigate = useNavigate();

  const [players, setPlayers] = useState<Player[]>([]);
  const [lobby, setLobby] = useState<any>(null);

  // queue holds player ids not yet assigned to matches
  const [queue, setQueue] = useState<string[]>([]);
  // matches is array of arrays of player ids
  const [matches, setMatches] = useState<string[][]>(() => [[], []]);

  useEffect(() => {
    if (!lobbyId) return;
    fetchLobbyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobbyId]);

  const fetchLobbyData = async () => {
    if (!lobbyId) return;
    try {
      const { data: lobbyData, error: lobbyErr } = await supabase
        .from('lobbies')
        .select('*')
        .eq('id', lobbyId)
        .single();

      if (lobbyErr) {
        console.error('Error fetching lobby:', lobbyErr);
      } else {
        setLobby(lobbyData);
      }

      const { data: playersData, error } = await supabase
        .from('players')
        .select('*')
        .eq('lobby_id', lobbyId)
        .order('joined_at', { ascending: true });

      if (error) {
        console.error('Error fetching players:', error);
      } else if (playersData) {
        const normalized = playersData.map((p: any) => ({ id: String(p.id), name: p.name }));
        setPlayers(normalized);
        setQueue(normalized.map((p: Player) => p.id));
        setMatches([[], []]);
      }
    } catch (err) {
      console.error('Unexpected fetch error:', err);
    }
  };

  const playerMap = useMemo(() => {
    const m = new Map<string, string>();
    players.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [players]);

  const [pickerOpenFor, setPickerOpenFor] = useState<number | null>(null);

  const allowDrop = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragStart = (e: React.DragEvent, playerId: string, from: string) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ playerId, from }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const movePlayer = (playerId: string, from: string, toMatchIndex: number | 'queue') => {
    setMatches((prev) => {
      const next = prev.map((m) => [...m]);
      if (from && from.startsWith('match:')) {
        const idx = parseInt(from.split(':')[1], 10);
        const pos = next[idx].indexOf(playerId);
        if (pos !== -1) next[idx].splice(pos, 1);
      }
      if (toMatchIndex !== 'queue') {
        if (!next[toMatchIndex].includes(playerId)) next[toMatchIndex].push(playerId);
      }
      return next;
    });

    setQueue((prev) => {
      let nextQ = prev.filter((id) => id !== playerId);
      if (toMatchIndex === 'queue') {
        if (!nextQ.includes(playerId)) nextQ = [...nextQ, playerId];
      }
      return nextQ;
    });
  };

  const handleDropOnMatch = (e: React.DragEvent, matchIndex: number) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    try {
      const { playerId, from } = JSON.parse(raw);
      movePlayer(playerId, from, matchIndex);
    } catch (err) {
      // ignore
    }
  };

  const handleDropOnQueue = (e: React.DragEvent) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData('text/plain');
    try {
      const { playerId, from } = JSON.parse(raw);
      movePlayer(playerId, from, 'queue');
    } catch (err) {
      // ignore
    }
  };

  const addMatch = () => setMatches((m) => [...m, []]);
  const removeMatch = (index: number) => {
    setMatches((prev) => {
      const next = prev.map((x) => [...x]);
      const removed = next.splice(index, 1)[0] || [];
      setQueue((q) => [...q, ...removed]);
      return next;
    });
  };

  const addPlayerFromQueueToMatch = (playerId: string, matchIndex: number) => {
    // move player from queue into the selected match
    movePlayer(playerId, 'queue', matchIndex);
    setPickerOpenFor(null);
  };

  const randomize = () => {
    const modeRaw = window.prompt('Randomize matches for "singles" (1v1) or "doubles" (2v2)? Enter "singles" or "doubles"', 'singles');
    if (!modeRaw) return;
    const mode = modeRaw.trim().toLowerCase();
    let matchSize = 2;
    if (mode === 'doubles' || mode === 'd') matchSize = 4;
    else if (mode === 'singles' || mode === 's') matchSize = 2;
    else {
      alert('Invalid option. Please enter "singles" or "doubles".');
      return;
    }

    const all = [...queue];
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }

    const numMatches = Math.floor(all.length / matchSize);
    if (numMatches === 0) {
      alert(`Not enough players to form a ${mode} match (need ${matchSize} players).`);
      return;
    }

    const nextMatches: string[][] = [];
    let idx = 0;
    for (let m = 0; m < numMatches; m++) {
      const group: string[] = [];
      for (let k = 0; k < matchSize; k++) {
        group.push(all[idx++]);
      }
      nextMatches.push(group);
    }

    const leftover = all.slice(idx);
    setMatches(nextMatches);
    setQueue(leftover);
  };

  const save = () => {
    console.log('Saved matches (creator):', matches.map((m) => m.map((id) => playerMap.get(id))));
    alert('Matches saved (local) — implement persistence if desired.');
  };

  return (
    <div className="min-h-screen px-6 pt-2 pb-6">
      <header className="mb-1">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate(-1)} className="px-3 py-2 rounded-full bg-gray-50">Back</button>
          </div>

          <div className="flex items-center space-x-3">
            <button onClick={randomize} className="text-sm px-3 py-2 rounded-full bg-yellow-50">Randomize</button>
            <button onClick={addMatch} className="text-sm px-3 py-2 rounded-full bg-blue-50">Add Match</button>
            <button onClick={() => navigate('/')} className="text-sm px-3 py-2 rounded-full bg-gray-50">Home</button>
          </div>
        </div>

        <div className="mt-1 text-center">
          <div className="text-sm text-gray-500">Lobby</div>
          <div className="text-lg font-semibold">{lobby?.name || 'Matchmaker'}</div>
        </div>
      </header>

      {pickerOpenFor !== null && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPickerOpenFor(null)} />
          <div role="dialog" aria-modal="true" className="relative bg-white rounded-lg shadow-lg w-full max-w-md p-4 z-50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Add player to Match {pickerOpenFor + 1}</h3>
              <button onClick={() => setPickerOpenFor(null)} className="text-sm px-2 py-1 rounded-full bg-gray-100">Close</button>
            </div>

            <div>
              {queue.length === 0 ? (
                <div className="text-sm text-gray-400">No players available</div>
              ) : (
                <div className="space-y-1 max-h-56 overflow-auto">
                  {queue.map((id) => (
                    <button
                      key={id}
                      onClick={() => addPlayerFromQueueToMatch(id, pickerOpenFor!)}
                      className="w-full text-left px-2 py-2 rounded hover:bg-gray-100"
                    >
                      {playerMap.get(id) || id}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Queue column */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium">Queue ({queue.length})</h4>
            <div className="text-sm text-muted">Drag to assign</div>
          </div>
          <div
            onDragOver={allowDrop}
            onDrop={handleDropOnQueue}
            className="min-h-[220px] p-3 rounded-xl border border-gray-100 bg-white/60"
          >
            {queue.length === 0 && <p className="text-sm text-gray-400">No players in queue</p>}
            {queue.map((id) => (
              <div
                key={id}
                draggable
                onDragStart={(e) => handleDragStart(e, id, 'queue')}
                className="mb-3 cursor-grab rounded-xl px-3 py-2 bg-white shadow-sm flex items-center justify-between"
              >
                <span className="text-sm">{playerMap.get(id) || id}</span>
                <span className="text-xs text-gray-400">≡</span>
              </div>
            ))}
          </div>
        </div>

        {/* Matches area */}
        <div className="md:col-span-2 col-span-1 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <h4 className="font-medium">Matches ({matches.length})</h4>
            <div className="text-sm text-gray-500 mt-2 md:mt-0">Drag players into match slots</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {matches.map((m, idx) => (
              <div key={idx} className="bg-white/70 rounded-xl border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <strong>Match {idx + 1}</strong>
                      </div>

                      <div className="flex items-center space-x-2">
                        <div className="relative">
                          <button
                            onClick={() => setPickerOpenFor(pickerOpenFor === idx ? null : idx)}
                            aria-label={`Add player to match ${idx + 1}`}
                            className="text-sm px-2 py-1 rounded-full bg-green-50 hover:bg-green-100"
                          >
                            +
                          </button>

                          
                        </div>

                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to remove this match?')) {
                              removeMatch(idx);
                            }
                          }}
                          className="text-xs text-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                <div
                  onDragOver={allowDrop}
                  onDrop={(e) => handleDropOnMatch(e, idx)}
                  className="min-h-[100px] p-3 border rounded-xl border-dashed border-gray-200 bg-gray-50"
                >
                  {m.length === 0 && <p className="text-sm text-gray-400">Drop players here</p>}
                  {m.map((id) => (
                    <div
                      key={id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, id, `match:${idx}`)}
                      className="mb-2 rounded-xl px-3 py-2 bg-white shadow-sm flex items-center justify-between"
                    >
                      <span className="text-sm">{playerMap.get(id) || id}</span>
                      <span className="text-xs text-gray-400">≡</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-end space-x-3">
            <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-full bg-gray-50">Cancel</button>
            <button onClick={save} className="px-4 py-2 rounded-full bg-green-500 text-white">Save Matches</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchmakingPage;
